import LinearProgress from '@/global/components/LinearProgress'
import { RadioGroup, RadioGroupItem } from '@/global/components/RadioGroup'
import Grid from '@/global/components/ui/Grid'
import { FormFieldSelect } from 'global/components/Form'
import IconeAjuda from 'global/components/IconeAjuda'
import { SelectEmpresa, SelectFuncionario } from 'global/components/Selects'
import Dialog from 'global/guides/Dialog'
import { hasPermission } from 'hooks/useBackendMenu'
import { useTransmit } from 'hooks/useTransmit'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { funcionario as funcionarioApi } from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import { ExportarService as useService } from '../../service/exportar'

type Formato = 'pdf' | 'xls' | 'csv'

type ExportarMessage =
    | { status: 'PROCESSANDO'; progresso: number }
    | { status: 'CONCLUIDO'; filename: string }
    | { status: 'ERRO'; message: string }

const ExportarFuncionario = () => {
    const navigate = useNavigate()
    const toggle = () => navigate('/cadastros/funcionarios')
    const funcionario = useStore(state => state.employee)
    const empresaId = useStore(state => state.companyId)
    const { handleExportarFicha, isCarregando } = useService()
    const [exportarTodos, setExportarTodos] = useState(true)
    const [formato, setFormato] = useState<Formato>('xls')
    const [progresso, setProgresso] = useState<number | null>(null)

    const subscribe = useTransmit<ExportarMessage>({
        onMessage: async (message, unsubscribe) => {
            if (message.status === 'PROCESSANDO') {
                setProgresso(message.progresso)
                return
            }

            if (message.status === 'CONCLUIDO') {
                try {
                    await funcionarioApi.exportar.file(message.filename)
                } catch (err) {
                    toast.error('Erro ao baixar o arquivo. Tente novamente.')
                    console.error('[Exportar] falha no download:', err)
                }
                await unsubscribe()
                setProgresso(null)
                return
            }

            if (message.status === 'ERRO') {
                toast.error(message.message)
                await unsubscribe()
                setProgresso(null)
            }
        },
    })

    const handleExportarTodos = async () => {
        const cod = window.crypto.randomUUID()
        let unsubscribe: (() => void | Promise<void>) | undefined

        setProgresso(0)
        try {
            unsubscribe = await subscribe(cod)

            const response = await funcionarioApi.exportar.post<
                { empresaId: number; formato: Formato; cod: string },
                { cod: string }
            >({ empresaId, formato, cod })

            if (response.cod !== cod) {
                throw new Error(
                    'Backend retornou um canal diferente do canal assinado.',
                )
            }
        } catch (error) {
            if (unsubscribe) {
                await unsubscribe()
            }

            toast.error('Erro ao iniciar exportação. Tente novamente.')
            console.error('[Exportar] falha ao iniciar exportação:', error)
            setProgresso(null)
        }
    }

    const isProcessando = progresso !== null

    return (
        <Dialog.Root
            loading={isCarregando || isProcessando}
            onClose={toggle}
            onSubmit={() => {
                if (exportarTodos) {
                    handleExportarTodos()
                } else {
                    handleExportarFicha(funcionario!, formato)
                }
            }}
            open={hasPermission(['read', 'ExportarFuncionários'])}
            width="3xl"
        >
            <Dialog.Title>
                Exportar funcionário
                <IconeAjuda>
                    Os dados incluídos no arquivo são:
                    <ul className="pl-4">
                        {[
                            'Matrícula',
                            'Nome',
                            'Cargo/Função',
                            'PIS',
                            'CPF',
                            'Data de admissão',
                        ].map((item, index) => (
                            <li key={index}>{item}</li>
                        ))}
                    </ul>
                    <br />
                    No entanto, é possível exportar apenas a ficha do
                    funcionário, que contém todos os dados cadastrados.
                </IconeAjuda>
            </Dialog.Title>
            <Dialog.Content className="w-full overflow-auto p-4">
                <Grid container className="mb-8">
                    <Grid item xs={6}>
                        <SelectEmpresa variant="form" />
                    </Grid>
                    <Grid
                        item
                        xs={6}
                        className="flex items-center justify-center"
                    >
                        <RadioGroup
                            aria-label="exportar"
                            defaultValue="todos"
                            name="exportar"
                            onValueChange={valor => {
                                setExportarTodos(valor === 'todos')
                            }}
                            className="flex flex-row items-center gap-6 mt-4"
                        >
                            <RadioGroupItem
                                value="todos"
                                label="Exportar todos"
                            />
                            <RadioGroupItem
                                value="ficha"
                                label="Exportar ficha"
                            />
                        </RadioGroup>
                    </Grid>
                    <Grid item xs={12}>
                        {!exportarTodos && <SelectFuncionario variant="form" />}
                    </Grid>
                    <Grid item xs={12}>
                        <FormFieldSelect
                            required
                            label="Formato de exportação"
                            name="formato"
                            defaultValue={'xls'}
                            options={[
                                {
                                    value: 'xls',
                                    label: 'Planilha (XLS)',
                                },
                                {
                                    value: 'pdf',
                                    label: 'Documento (PDF)',
                                },
                                {
                                    value: 'csv',
                                    label: 'Texto (CSV)',
                                },
                            ]}
                            onChange={valor => {
                                setFormato(valor as Formato)
                            }}
                        />
                    </Grid>
                    {isProcessando && (
                        <Grid item xs={12}>
                            <LinearProgress
                                variant="determinate"
                                value={progresso ?? 0}
                                className="rounded"
                            />
                        </Grid>
                    )}
                </Grid>
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton disabled={isProcessando} />
                <Dialog.Actions.PrimaryButton disabled={isProcessando}>
                    {isProcessando ? 'Exportando...' : 'Exportar'}
                </Dialog.Actions.PrimaryButton>
            </Dialog.Actions>
        </Dialog.Root>
    )
}

export default ExportarFuncionario
